import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Pencil, Power, PowerOff, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatCurrency } from "@/lib/currency";
import { ROUTES } from "@/config/routes";
import { TIER_COLORS, type ProductLineListDto } from "@/types/product-line";
import {
  PRODUCT_LINE_COLUMNS,
  useProductLineColumnsStore,
  type ProductLineColumnId,
} from "@/stores/productLineColumnsStore";

/**
 * How wide each column is drawn. A full `Record` over the id union, so adding a column without a
 * width is a compile error rather than a column that silently collapses.
 */
const COLUMN_WIDTHS: Record<ProductLineColumnId, string> = {
  tier: "w-36",
  name: "w-72",
  cpu: "w-40",
  ram: "w-40",
  ssd: "w-40",
  products: "w-28",
  status: "w-28",
  actions: "w-24",
};

interface ProductLineTableProps {
  productLines: ProductLineListDto[];
  isLoading?: boolean;
  /** Catalog writes are SuperAdmin + BillingAdmin; everyone else gets no actions menu. */
  canEdit: boolean;
  isUpdating: boolean;
  onActivate: (productLine: ProductLineListDto) => void;
  onDeactivate: (productLine: ProductLineListDto) => void;
  onDelete: (productLine: ProductLineListDto) => void;
}

export function ProductLineTable({
  productLines,
  isLoading = false,
  canEdit,
  isUpdating,
  onActivate,
  onDeactivate,
  onDelete,
}: ProductLineTableProps) {
  const navigate = useNavigate();

  // Subscribe to `hidden` and derive the predicate locally — the store's own `isVisible` is a
  // stable function and would never re-render this table on a toggle.
  const hiddenColumns = useProductLineColumnsStore((state) => state.hidden);
  const isVisible = (id: ProductLineColumnId) => !hiddenColumns.includes(id);

  if (isLoading) {
    return (
      <div className="min-h-0 flex-1 space-y-3 overflow-hidden rounded-md border p-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    // The scroll lives on this height-bounded box; `[&>div]:overflow-visible` switches off the
    // wrapper `ui/table.tsx` adds, whose scrollbar would render below the last row and whose
    // scrollport would stop the sticky header from sticking.
    <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-md border [&>div]:overflow-visible">
      <Table className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
        {/* Generated from the column list, so a hidden column cannot leave its <col> behind. */}
        <colgroup>
          {PRODUCT_LINE_COLUMNS.filter((column) => isVisible(column.id)).map((column) => (
            <col key={column.id} className={COLUMN_WIDTHS[column.id]} />
          ))}
        </colgroup>
        <TableHeader className="sticky top-0 z-30 bg-card">
          <TableRow>
            {isVisible("tier") && <TableHead>Tier</TableHead>}
            {isVisible("name") && <TableHead>Name</TableHead>}
            {isVisible("cpu") && <TableHead className="text-right">CPU / core</TableHead>}
            {isVisible("ram") && <TableHead className="text-right">RAM / GB</TableHead>}
            {isVisible("ssd") && <TableHead className="text-right">SSD / GB</TableHead>}
            {isVisible("products") && <TableHead className="text-center">Products</TableHead>}
            {isVisible("status") && <TableHead className="text-center">Status</TableHead>}
            {isVisible("actions") && (
              <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                Actions
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {productLines.map((productLine) => (
            <TableRow key={productLine.id}>
              {isVisible("tier") && (
                <TableCell>
                  <Badge variant="outline" className={TIER_COLORS[productLine.tier]}>
                    {productLine.tier}
                  </Badge>
                </TableCell>
              )}
              {isVisible("name") && (
                <TableCell>
                  <div className="space-y-0.5">
                    <span
                      className="block truncate font-medium text-foreground"
                      title={productLine.name}
                    >
                      {productLine.name}
                    </span>
                    <p
                      className="truncate font-mono text-[11px] text-muted-foreground"
                      title={productLine.slug}
                    >
                      {productLine.slug}
                    </p>
                  </div>
                </TableCell>
              )}
              {isVisible("cpu") && (
                <TableCell className="text-right tabular-nums">
                  {formatCurrency(productLine.pricePerCpuCore)}
                </TableCell>
              )}
              {isVisible("ram") && (
                <TableCell className="text-right tabular-nums">
                  {formatCurrency(productLine.pricePerRamGb)}
                </TableCell>
              )}
              {isVisible("ssd") && (
                <TableCell className="text-right tabular-nums">
                  {formatCurrency(productLine.pricePerSsdGb)}
                </TableCell>
              )}
              {isVisible("products") && (
                <TableCell className="text-center tabular-nums">
                  {productLine.productCount}
                </TableCell>
              )}
              {isVisible("status") && (
                <TableCell className="text-center">
                  <Badge variant={productLine.isActive ? "default" : "secondary"}>
                    {productLine.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
              )}
              {isVisible("actions") && (
                <TableCell className="sticky right-0 z-10 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                  {canEdit ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="Actions"
                          aria-label={`Actions for ${productLine.name}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => navigate(ROUTES.productLines.edit(productLine.id))}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        {productLine.isActive ? (
                          <DropdownMenuItem
                            disabled={isUpdating}
                            onClick={() => onDeactivate(productLine)}
                          >
                            <PowerOff className="mr-2 h-4 w-4" />
                            Deactivate
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            disabled={isUpdating}
                            onClick={() => onActivate(productLine)}
                          >
                            <Power className="mr-2 h-4 w-4" />
                            Activate
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive-ink"
                          onClick={() => onDelete(productLine)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
